<template>
  <div v-if="planIsActive && plan.products">
    <h1 class="text-xl py-4 pl-2 font-extrabold tracking-tight text-gray-900">Produits assurés</h1>
    <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      <li
        v-for="product in plan.products"
        :key="product.id"
        class="bg-white col-span-1 flex flex-col text-center rounded-lg shadow-sm divide-y divide-gray-200"
      >
        <div class="flex-1 flex flex-col p-4">
          <div class="flex flex-row justify-between mb-2">
            <div
              v-if="plan.composedProductType && planIsActive && composedItemsMutable"
              @click="updateItem(product)"
              class="w-5 h-5 text-primary cursor-pointer hover:underline"
              aria-hidden="true"
            >
              <span>Modifier</span>
            </div>
            <TrashIcon
              v-if="this.plan.products?.length > 1 && planIsActive && composedItemsMutable"
              @click="deleteItem(product)"
              class="w-5 h-5 text-red-400 cursor-pointer hover:text-red-900"
              aria-hidden="true"
            />
          </div>
          <img
            v-if="product?.imageUrl"
            class="max-h-32 object-contain shrink-0 mx-auto"
            :src="`${product.imageUrl}`"
            :alt="product.title || $t('insuredProducts.productImageAlt')"
          />
          <div class="my-4">
            <h3 class="mt-1 text-gray-900 text-md font-bold">
              {{ product.title }}
            </h3>
          </div>
          <div>
            <div v-if="product.itemType" class="mt-1 flex flex-row justify-between text-sm">
              <span>Catégorie</span>
              <span class="font-bold">{{ $t(product.itemType) }}</span>
            </div>
            <template v-if="!isVirtualProduct(product)">
              <div class="mt-1 flex flex-row justify-between text-sm">
                <span>Marque</span>
                <span class="font-bold">{{ product.vendor }}</span>
              </div>
              <div v-if="product.model" class="mt-1 flex flex-row justify-between text-sm">
                <span>Modèle</span>
                <span class="font-bold">{{ product.model }}</span>
              </div>
              <div class="mt-1 flex flex-row justify-between text-sm">
                <span>Prix du produit</span>
                <span class="font-bold">{{ formatPrice(product.price) }}</span>
              </div>
            </template>
            <div class="mt-1 flex flex-row justify-between text-sm">
              <span>{{ isVirtualProduct(product) ? "Référence" : "Numéro de série" }}</span>
              <span class="font-bold">{{ product.referenceId }}</span>
            </div>
            <div class="mt-1 flex flex-row justify-between text-sm">
              <span>Date d'achat</span>
              <span class="font-bold">{{ product.purchaseDate }}</span>
            </div>
          </div>
        </div>
        <template v-if="planIsActive">
          <div class="-mt-px flex divide-x divide-gray-200">
            <div class="cursor-pointer w-0 flex-1 flex">
              <router-link
                class="relative w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-br-lg hover:text-gray-500"
                :to="newClaimPath(isVirtualProduct(product) ? null : product.id)"
              >
                <ShieldCheckIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                <span class="ml-3">Déclarer un sinistre</span>
              </router-link>
            </div>
          </div>
          <div class="-mt-px flex divide-x divide-gray-200">
            <div class="cursor-pointer w-0 flex-1 flex" v-if="productExchangeAllowed(plan, claims)">
              <button
                class="relative w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-br-lg hover:text-gray-500"
                @click="openExchangeModal"
              >
                <PencilIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                <span class="ml-3">Déclarer un échange de produit</span>
              </button>
            </div>
          </div>
        </template>
      </li>
      <li
        v-if="plan.composedProductType && planIsActive && composedItemsMutable"
        class="col-span-1 flex flex-col text-center rounded-lg border-2 border-dashed border-primary cursor-pointer group transition-all duration-200 hover:border-primary-dark hover:shadow-md"
        @click="addItem"
      >
        <div class="py-10 flex flex-col items-center justify-center min-h-full">
          <PlusIcon
            class="w-10 h-10 text-primary group-hover:text-primary-dark transition-colors duration-200"
            aria-hidden="true"
          />
          <span
            class="mt-4 text-primary group-hover:text-primary-dark text-sm font-semibold transition-colors duration-200"
          >
            Enregistrer un produit
          </span>
        </div>
      </li>
    </ul>
  </div>
  <DeleteProductItemModal
    :isOpen="isDeleteModalOpen"
    :productItem="deleteProductItem"
    :planSaleId="plan.id"
    @closeDeleteProductItemModal="closeDeleteProductItemModal"
    @refreshPlanSale="refreshPlanSale"
  />
  <AddProductItemModal
    :isOpen="isAddModalOpen"
    :plan="plan"
    @closeAddProductItemModal="closeAddProductItemModal"
    @refreshPlanSale="refreshPlanSale"
  />
  <UpdateProductItemModal
    :isOpen="isUpdateModalOpen"
    :plan="plan"
    :productItem="updateProductItem"
    @closeUpdateProductItemModal="closeUpdateProductItemModal"
    @refreshPlanSale="refreshPlanSale"
  />
  <ProductExchangeFormModal
    v-if="productExchangeAllowed(plan, claims)"
    :open="productExchangeFormModalOpen"
    :plan="plan"
    @close="productExchangeFormModalOpen = false"
    @refreshPlanSale="refreshPlanSale"
  />
</template>

<script>
import { formatPrice, isVirtualProduct } from "@estaly/ui";
import { mapState, mapGetters } from "vuex";
import { ShieldCheckIcon, TrashIcon, PlusIcon, PencilIcon } from "@heroicons/vue/solid";
import DeleteProductItemModal from "@/views/plan/DeleteProductItemModal";
import UpdateProductItemModal from "@/views/plan/UpdateProductItemModal";
import AddProductItemModal from "@/views/plan/AddProductItemModal";
import ProductExchangeFormModal from "@/components/plan/ProductExchangeFormModal.vue";

export default {
  props: {
    plan: { type: Object, required: true },
    claims: { type: Array, default: null },
  },

  components: {
    ProductExchangeFormModal,
    ShieldCheckIcon,
    TrashIcon,
    PlusIcon,
    PencilIcon,
    DeleteProductItemModal,
    AddProductItemModal,
    UpdateProductItemModal,
  },

  data() {
    return {
      isDeleteModalOpen: false,
      isUpdateModalOpen: false,
      productExchangeFormModalOpen: false,
      isAddModalOpen: false,
      deleteProductItem: null,
      updateProductItem: null,
    };
  },

  computed: {
    ...mapState("customer", ["plans"]),
    ...mapGetters("planSales", ["isActive", "productExchangeAllowed"]),
    ...mapGetters("plan", ["isComposedItemsMutable"]),
    planIsActive() {
      return this.isActive(this.plan);
    },
    composedItemsMutable() {
      return this.isComposedItemsMutable(this.plan);
    },
  },

  methods: {
    formatPrice,
    isVirtualProduct,

    newClaimPath(productId) {
      const q_params = new URLSearchParams();
      q_params.append("plan", this.plan.friendlyId);
      if (productId) q_params.append("product_id", productId);
      return `/file-claim?${q_params}`;
    },

    deleteItem(product) {
      this.deleteProductItem = product;
      this.isDeleteModalOpen = true;
    },

    addItem() {
      this.isAddModalOpen = true;
    },

    updateItem(product) {
      this.updateProductItem = product;
      this.isUpdateModalOpen = true;
    },

    closeDeleteProductItemModal() {
      this.isDeleteModalOpen = false;
    },

    closeUpdateProductItemModal() {
      this.isUpdateModalOpen = false;
    },

    closeAddProductItemModal() {
      this.isAddModalOpen = false;
    },

    openExchangeModal() {
      this.productExchangeFormModalOpen = true;
    },

    refreshPlanSale() {
      this.$emit("refreshPlanSale");
    },
  },

  watch: {
    plan: {
      deep: true,
    },
  },
};
</script>
